import { useCallback, useEffect, useMemo, useState } from 'react'

import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { isFailed, isPending, isSuccess } from 'redux/toolkit/api'
import { getOutboundIps } from 'redux/features/outboundIp/outboundIpSlice'
import { getAccountSettings, updateAccountSettings } from 'redux/features/settings/settingsSlice'
import { useDomainPolicyAccess } from 'lib/hooks/useDomainPolicyAccess'
import { AvailableSettings, SettingValue } from 'types/Settings'

const OUTBOUND_RELAY_SETTINGS = [AvailableSettings.DISABLE_SHARED_IP_RANGES]

export interface State {
  isLoading: boolean
  isFailed: boolean
  isSuccess: boolean
  isDirtyForm: boolean
  isDisabledSubmit: boolean
  hasNoDomainPolicyAccess: boolean
  outboundRelay: {
    isHidden: boolean
    isEnabled: boolean
    isLoading: boolean
  }
}

export interface EventHandlers {
  helpConfig: {
    isOpen: boolean
    onHelpClick: () => void
    onCloseHelp: () => void
  }
  setIsTableDirty: (isDirty: boolean) => void
  onOutboundRelayToggle: () => void
  onSave: () => void
  onCancel: () => void
}

export type UseSenderIpLogic = [State, EventHandlers]

export const useSenderIpLogic = (): UseSenderIpLogic => {
  const dispatch = useAppDispatch()
  const [isTableDirty, setIsTableDirty] = useState(false)
  const [isHelpDialogOpened, setIsHelpDialogOpened] = useState<boolean>(false)
  const [localOutboundRelayEnabled, setLocalOutboundRelayEnabled] = useState<boolean | null>(null)

  const {
    pdDomainId,
    isGetOutboundIpsPending,
    isGetOutboundIpsSuccess,
    isGetOutboundIpsFailed,
    isRemoveOutboundIpsPending,
    isRemoveOutboundIpsSuccess,
    isAddOutboundIpsPending,
    isAddOutboundIpsSuccess,
    accessTokenObject,
    accountSettings,
    isGetAccountSettingsPending,
    isUpdateAccountSettingsPending,
    isUpdateAccountSettingsSuccess,
    featureFlags
  } = useAppSelector(_stores => ({
    pdDomainId: _stores.auth.accessTokenObject?.pdDomainId,
    accessTokenObject: _stores.auth.accessTokenObject,
    featureFlags: _stores.auth.accessTokenObject?.featureFlags || {},
    outboundIps: _stores.outboundIp.outboundIps,
    isGetOutboundIpsPending: isPending(_stores.outboundIp.getOutboundIpApiStatus),
    isGetOutboundIpsSuccess: isSuccess(_stores.outboundIp.getOutboundIpApiStatus),
    isGetOutboundIpsFailed: isFailed(_stores.outboundIp.getOutboundIpApiStatus),
    isRemoveOutboundIpsPending: isPending(_stores.outboundIp.removeOutboundIpApiStatus),
    isRemoveOutboundIpsSuccess: isSuccess(_stores.outboundIp.removeOutboundIpApiStatus),
    isAddOutboundIpsPending: isPending(_stores.outboundIp.addOutboundIpApiStatus),
    isAddOutboundIpsSuccess: isSuccess(_stores.outboundIp.addOutboundIpApiStatus),
    accountSettings: _stores.settings.accountSettings,
    isGetAccountSettingsPending: isPending(_stores.settings.getAccountSettingsApiStatus),
    isUpdateAccountSettingsPending: isPending(_stores.settings.updateAccountSettingsApiStatus),
    isUpdateAccountSettingsSuccess: isSuccess(_stores.settings.updateAccountSettingsApiStatus)
  }))

  // Domain policy access check - only applies in domain-manage mode (when pdDomainId is set)
  const isInDomainManageMode = !!pdDomainId
  const { hasNoDomainPolicyAccess: rawHasNoDomainPolicyAccess } = useDomainPolicyAccess(
    Number(accessTokenObject?.pdDomainId) || 0
  )
  // Only block access when in domain-manage mode AND user has no R or U access
  const hasNoDomainPolicyAccess = isInDomainManageMode && rawHasNoDomainPolicyAccess

  // Outbound relay toggle - only show at account level (not in domain-manage mode) and when feature flag is ON
  const isFeatureFlagEnabled = !!featureFlags.FEATURE_FLAG_DISABLE_SHARED_IP_RANGES_OFF
  const isOutboundRelayHidden = isInDomainManageMode || !isFeatureFlagEnabled
  // disable_shared_ip_ranges: '0' = enabled (use shared IPs), '1' = disabled (don't use shared IPs)
  // When undefined (not in DB or deleted), default to enabled - but only when toggle is visible
  const serverOutboundRelayEnabled = isOutboundRelayHidden
    ? false
    : accountSettings[AvailableSettings.DISABLE_SHARED_IP_RANGES] !== SettingValue.ENABLED
  // Use local state if set, otherwise use server state
  const isOutboundRelayEnabled = localOutboundRelayEnabled ?? serverOutboundRelayEnabled
  const isOutboundRelayDirty =
    localOutboundRelayEnabled !== null && localOutboundRelayEnabled !== serverOutboundRelayEnabled

  // init
  useEffect(() => {
    if (hasNoDomainPolicyAccess) {
      return
    }
    if (pdDomainId) {
      dispatch(getOutboundIps({ pdDomainId }))
    } else {
      dispatch(getOutboundIps({}))
      // Fetch account settings for the outbound relay toggle (only at account level and when feature flag is enabled)
      if (isFeatureFlagEnabled) {
        dispatch(getAccountSettings(OUTBOUND_RELAY_SETTINGS))
      }
    }
    // eslint-disable-next-line
  }, [pdDomainId, hasNoDomainPolicyAccess, isFeatureFlagEnabled])

  // Refetch settings after successful update and reset local state
  useEffect(() => {
    if (isUpdateAccountSettingsSuccess && !isInDomainManageMode) {
      dispatch(getAccountSettings(OUTBOUND_RELAY_SETTINGS))
      setLocalOutboundRelayEnabled(null)
    }
    // eslint-disable-next-line
  }, [isUpdateAccountSettingsSuccess])

  useEffect(() => {
    if (isRemoveOutboundIpsSuccess || isAddOutboundIpsSuccess) {
      if (pdDomainId) {
        dispatch(getOutboundIps({ pdDomainId }))
      } else {
        dispatch(getOutboundIps({}))
      }
    }
    // eslint-disable-next-line
  }, [isRemoveOutboundIpsSuccess, isAddOutboundIpsSuccess])

  const onHelpClick = useCallback(() => {
    setIsHelpDialogOpened(true)
  }, [])

  const onCloseHelp = useCallback(() => {
    setIsHelpDialogOpened(false)
  }, [])

  const onOutboundRelayToggle = useCallback(() => {
    setLocalOutboundRelayEnabled(!isOutboundRelayEnabled)
  }, [isOutboundRelayEnabled])

  const onSave = useCallback(() => {
    if (localOutboundRelayEnabled !== null && localOutboundRelayEnabled !== serverOutboundRelayEnabled) {
      const newValue = localOutboundRelayEnabled ? SettingValue.DISABLED : SettingValue.ENABLED
      dispatch(
        updateAccountSettings({
          settings: {
            [AvailableSettings.DISABLE_SHARED_IP_RANGES]: newValue
          }
        })
      )
    }
  }, [dispatch, localOutboundRelayEnabled, serverOutboundRelayEnabled])

  const onCancel = useCallback(() => {
    setLocalOutboundRelayEnabled(null)
  }, [])

  const isDirtyForm = isTableDirty || isOutboundRelayDirty
  const isDisabledSubmit = !isOutboundRelayDirty || isUpdateAccountSettingsPending

  return useMemo(
    () => [
      {
        isLoading: isGetOutboundIpsPending || isRemoveOutboundIpsPending || isAddOutboundIpsPending,
        isFailed: isGetOutboundIpsFailed,
        isSuccess: isGetOutboundIpsSuccess,
        isDirtyForm,
        isDisabledSubmit,
        hasNoDomainPolicyAccess,
        outboundRelay: {
          isHidden: isOutboundRelayHidden,
          isEnabled: isOutboundRelayEnabled,
          isLoading: isGetAccountSettingsPending || isUpdateAccountSettingsPending
        }
      },
      {
        helpConfig: {
          isOpen: isHelpDialogOpened,
          onHelpClick,
          onCloseHelp
        },
        setIsTableDirty,
        onOutboundRelayToggle,
        onSave,
        onCancel
      }
    ],
    [
      isGetOutboundIpsPending,
      isRemoveOutboundIpsPending,
      isAddOutboundIpsPending,
      isGetOutboundIpsFailed,
      isGetOutboundIpsSuccess,
      isDirtyForm,
      isDisabledSubmit,
      isHelpDialogOpened,
      onHelpClick,
      onCloseHelp,
      hasNoDomainPolicyAccess,
      isOutboundRelayHidden,
      isOutboundRelayEnabled,
      isGetAccountSettingsPending,
      isUpdateAccountSettingsPending,
      onOutboundRelayToggle,
      onSave,
      onCancel
    ]
  )
}
